import { Validator } from 'fluentvalidation-ts';
import { MultiSelectOptionType } from '../../shared/form/inputs/MultiSearchSelect';
import { beAnInteger, beValidSequence, beValidUrl } from '../../validators/stringValidators';
import { DocumentResponse } from './documentResponse';

export type DocumentFormModel = {
  documentReference: string;
  documentName: string;
  customerName: string;
  documentUrl: string;
  documentVersion: string;
  handoverPackSectionId: number;
  handoverPackSectionSequence: string;
  parts: Array<MultiSelectOptionType<string>>;
  secondaryParts: Array<MultiSelectOptionType<string>>;
  isAlwaysIncluded: boolean;
  useNuHeatSuppliedHeatPump: boolean;
  useSolar: boolean;
  useUnderfloorHeating: boolean;
  useUserSuppliedHeatPump: boolean;
  useBoiler: boolean;
  useAnyTechnology: boolean;
  matchAllParts: boolean;
  includeInContentsPage: boolean;
  includeInPrintFolder: boolean;
  prePrinted: boolean;
  neverPrint: boolean;
};

export const getInitialDocumentFormModelValues = (defaultHandoverPackSectionId: number): DocumentFormModel => ({
  documentReference: '',
  documentName: '',
  customerName: '',
  documentUrl: '',
  documentVersion: '',
  handoverPackSectionId: defaultHandoverPackSectionId,
  handoverPackSectionSequence: '',
  parts: [],
  secondaryParts: [],
  isAlwaysIncluded: false,
  useNuHeatSuppliedHeatPump: false,
  useSolar: false,
  useUnderfloorHeating: false,
  useUserSuppliedHeatPump: false,
  useBoiler: false,
  useAnyTechnology: false,
  matchAllParts: false,
  includeInContentsPage: true,
  includeInPrintFolder: false,
  prePrinted: false,
  neverPrint: false,
});

export const mapDocumentResponseToFormModel = (document: DocumentResponse): DocumentFormModel => ({
  ...document,
  documentVersion: document.documentVersion === null ? '' : document.documentVersion.toString(),
  handoverPackSectionId: document.handoverPackSectionId,
  parts: document.parts.map(part => ({ value: part.partNumber, label: part.partName })),
  secondaryParts: document.secondaryParts.map(part => ({ value: part.partNumber, label: part.partName })),
});

export const mapDocumentResponseToCopyFormModel = (document: DocumentResponse): DocumentFormModel => ({
  ...document,
  documentReference: '',
  documentName: '',
  customerName: '',
  documentVersion: '',
  documentUrl: '',
  handoverPackSectionId: document.handoverPackSectionId,
  parts: document.parts.map(part => ({ value: part.partNumber, label: part.partName })),
  secondaryParts: document.secondaryParts.map(part => ({ value: part.partNumber, label: part.partName })),
});

export const getPartIdsAndDockingTypeCodesFromSelectedParts = (parts: Array<MultiSelectOptionType<string>>) => {
  const partIds = parts.filter(part => !isNaN(Number(part.value))).map(part => Number(part.value));
  // Docking codes follow format "d:{code}" to avoid clash with part ID, so remove the "d:" prefix before sending to API
  const dockingTypeCodes = parts.filter(part => isNaN(Number(part.value))).map(part => part.value.slice(2));

  return { partIds, dockingTypeCodes };
};

class DocumentFormModelValidator extends Validator<DocumentFormModel> {
  constructor() {
    super();

    this.ruleFor('documentReference')
      .notEmpty()
      .withMessage('Please enter a document reference')
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('documentName')
      .notEmpty()
      .withMessage('Please enter a document name')
      .maxLength(200)
      .withMessage('Please enter no more than 200 characters');

    this.ruleFor('customerName')
      .notEmpty()
      .withMessage('Please enter a customer name')
      .maxLength(100)
      .withMessage('Please enter no more than 100 characters');

    this.ruleFor('documentUrl')
      .notEmpty()
      .withMessage('Please enter a document URL')
      .maxLength(500)
      .withMessage('Please enter no more than 500 characters')
      .must(beValidUrl);

    this.ruleFor('documentVersion')
      .must(beAnInteger)
      .unless(model => model.documentVersion.trim() === '');

    this.ruleFor('handoverPackSectionId').notEqual(0);

    this.ruleFor('handoverPackSectionSequence').must(beValidSequence);
  }
}

export const documentFormModelValidator = new DocumentFormModelValidator();
